import type { ActionConfig } from "common/contentActions";
import {
  QueryAdapterOptions,
  QueryAdapterValue,
  gql,
} from "common/graphqlAdapter";
import {
  ItemType,
  JobDetailType,
  LearningFragment,
  LearningSummaryType,
} from "@devforce/trailhead-client-gql";

import type { Variant as CardVariant } from "tds/card";
import type { Layout } from "tds/contentCollectionCard";
import type { Config as LoadingStateConfig } from "tds/contentCollectionLoadingState";
import type { ContentSummaryConfig, ContentSummary } from "tds/contentSummary";

import {
  fromCommunity,
  fromJob,
  fromLearning,
  fromUnified,
} from "common/contentSummary";
import type { LearningType, LearningCollectionItem } from "common/learning";

export type CommunityNodeSummaryType = {
  id: string;
  type: string;
  name: string;
  typeLocalized: string;
  avatar: string;
  description: string;
  url: string;
};

export type CommunityNodeSummariesType = CommunityNodeSummaryType[];

export type CollectionChangeEvent = CustomEvent<{
  state: LoadingStateConfig["state"];
  items?: ContentSummary[];
}>;

export type PersonalizationConfig = {
  googleClientId?: string;
  campaignName?: string;
  fallbackItems?: LearningCollectionItem[];
  allowEnglishFallbacks?: boolean;
};

export type CollectionConfig = {
  card?: {
    ctaAlways?: boolean;
    ctaDisabled?: boolean;
    ctaHref?: string;
    ctaLabel?: string;
    ctaSpinner?: boolean;
    ctaTarget?: string;
    ctaVariant?: string;
    description?: string;
    layout?: Layout;
    title?: string;
    variant?: CardVariant;
  };
  item?: ContentSummaryConfig;
  limit?: number;
  state?: Omit<LoadingStateConfig, "state">;
  personalization?: PersonalizationConfig;
};

export type CollectionItem = {
  action?: ActionConfig;
  summary: ContentSummary;
};

export interface CollectionState {
  endCursor?: string | null;
  startCursor?: string | null;
}

export interface CollectionQuery<
  T,
  S extends CollectionState = CollectionState
> {
  getCollectionItems(result: T): CollectionItem[];
  getQueryOptions?(state?: S): QueryAdapterOptions;
  getTotalItemCount?(result?: T): number | undefined;
  getState?(result: T): S | undefined;
  hasMore?(result: T): boolean | undefined;
  /**
   * @deprecated use getQueryOptions()
   */
  queryOptions?: QueryAdapterOptions;
  useFetch?: boolean;
}

export type InteractionStudioRecommendationItemType = {
  contentItem: LearningSummaryType;
};

export type PersonalizedLearningQueryResult = QueryAdapterValue<{
  visitorRecommendations: {
    recommendations: InteractionStudioRecommendationItemType[];
    recommendationAnalyticsMetadata: Record<string, unknown>;
    interactionStudioMetadata: Record<string, unknown>;
  };
}>;
export class PersonalizedLearningQuery
  implements CollectionQuery<PersonalizedLearningQueryResult>
{
  options: {
    campaignName?: string;
    userContext: {
      googleClientId: string | undefined;
    };
    fallbackItems: { name: string; type: string }[] | undefined;
    allowEnglishFallbacks?: boolean;
  } = null!;

  useFetch = true;
  recommendationAnalyticsMetadata = {};
  interActionStudioMetadata = {};

  constructor(options: PersonalizedLearningQuery["options"]) {
    this.options = options;
  }

  private query = gql`
    ${LearningFragment}
    query ThexternalPersonalizedLearning(
      $campaignName: String!
      $userContext: RecommendationUserContext!
      $fallbackItems: [RecommendationFallbackInput!]
      $allowEnglishFallbacks: Boolean!
    ) {
      visitorRecommendations(
        campaignName: $campaignName
        userContext: $userContext
        fallbackItems: $fallbackItems
        allowEnglishFallbacks: $allowEnglishFallbacks
      ) {
        recommendations {
          contentItem {
            ...Learning
          }
        }
        interactionStudioMetadata {
          campaignId
          campaignName
          experienceId
          experienceName
          googleClientId
          itemType
          userGroup
        }
        recommendationAnalyticsMetadata {
          englishFallbacksRendered
          fallbackItemsRendered {
            name
            type
          }
        }
      }
    }
  `;

  getQueryOptions() {
    const { campaignName, userContext, fallbackItems, allowEnglishFallbacks } =
      this.options;

    return {
      query: this.query,
      variables: {
        campaignName,
        userContext,
        fallbackItems,
        allowEnglishFallbacks,
      },
    };
  }

  getCollectionItems(result: PersonalizedLearningQueryResult) {
    const edges = result.data.visitorRecommendations.recommendations ?? [];

    this.recommendationAnalyticsMetadata =
      result.data.visitorRecommendations.recommendationAnalyticsMetadata;
    this.interActionStudioMetadata =
      result.data.visitorRecommendations.interactionStudioMetadata;

    return edges
      .map((edge) => edge.contentItem)
      .map((learning) => getCollectionItem(learning));
  }
}

export const DEFAULT_LIMIT = 3;

export function createConfig(
  config: CollectionConfig | null,
  defaultConfig?: CollectionConfig | null
): CollectionConfig {
  return {
    ...defaultConfig,
    ...config,
    card: {
      layout: "stack",
      variant: "default",
      ...defaultConfig?.card,
      ...config?.card,
    },
    item: {
      variant: "brick",
      ...defaultConfig?.item,
      ...config?.item,
    },
    limit: config?.limit ?? defaultConfig?.limit ?? DEFAULT_LIMIT,
    state: {
      ...defaultConfig?.state,
      ...config?.state,
      loading: {
        stencilCount: config?.limit ?? defaultConfig?.limit ?? DEFAULT_LIMIT,
        ...defaultConfig?.state?.loading,
        ...config?.state?.loading,
        stencilVariant:
          config?.state?.loading?.stencilVariant ??
          config?.item?.variant ??
          "brick",
        preserveLayout: config?.state?.loading?.preserveLayout,
      },
    },
  };
}

export function getCollectionItem(
  learning: LearningType,
  user?: { username: string },
  eventData?: { name: string; id: string }
): CollectionItem {
  return {
    action: { learning, user, eventData },
    summary: fromLearning(learning),
  };
}

export function getCommunityCollectionItem(
  communityItem: CommunityNodeSummaryType
): CollectionItem {
  return {
    summary: fromCommunity(communityItem),
  };
}

export function getJobCollectionItem(job: JobDetailType): CollectionItem {
  return {
    summary: fromJob(job),
  };
}

export function getUnifiedCollectionItem(
  unified: ItemType
): CollectionItem | null {
  const summary = fromUnified(unified);
  if (summary == null) return null;

  return {
    action: undefined,
    summary,
  };
}
